import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import InternalLayout from "@/components/InternalLayout";
import PageTracker from "@/components/PageTracker";

import Landing from "@/pages/Landing";
import NewAnalysis from "@/pages/NewAnalysis";
import Report from "@/pages/Report";
import Recent from "@/pages/Recent";
import About from "@/pages/About";
import Disclaimer from "@/pages/Disclaimer";
import Pricing from "@/pages/Pricing";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import WhoItsFor from "@/pages/WhoItsFor";
import Dashboard from "@/pages/Dashboard";
import PreviewToken from "@/pages/PreviewToken";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import InternalDashboard from "@/pages/internal/Dashboard";
import Prospects from "@/pages/internal/Prospects";
import ProspectNew from "@/pages/internal/ProspectNew";
import ProspectDetail from "@/pages/internal/ProspectDetail";
import CRM from "@/pages/internal/CRM";
import Knowledge from "@/pages/internal/Knowledge";
import Customers from "@/pages/internal/Customers";
import Team from "@/pages/internal/Team";
import Operations from "@/pages/internal/Operations";
import AiOps from "@/pages/internal/AiOps";

// Phase 1 — Public Marketing & Education
import HowItWorks from "@/pages/public/HowItWorks";
import Tour from "@/pages/public/Tour";
import UseCases from "@/pages/public/UseCases";
import Learning from "@/pages/public/Learning";
import LearningArticle from "@/pages/public/LearningArticle";
import Sample from "@/pages/public/Sample";
import FAQ from "@/pages/public/FAQ";
import StrategyCall from "@/pages/public/StrategyCall";
import ProposalServices from "@/pages/public/ProposalServices";
import Discovery from "@/pages/public/Discovery";
import DemoLibrary from "@/pages/public/DemoLibrary";
import AskAIWidget from "@/components/public/AskAIWidget";

// Phase 3 — Customer Platform (magic-link auth, owned reports, copilot)
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import CustomerLayout, { CustomerProtectedRoute } from "@/components/customer/CustomerLayout";
import CustomerLogin, { CustomerVerify } from "@/pages/customer/Login";
import CustomerDashboard from "@/pages/customer/Dashboard";
import { CustomerPursuits, CustomerPursuitDetail, CustomerPursuitNew } from "@/pages/customer/Pursuits";
import CustomerPursuitBrief from "@/pages/customer/PursuitBrief";
import PublicBrief from "@/pages/public/PublicBrief";
import EosGate from "@/pages/eos/EosGate";
import EosInbox from "@/pages/eos/EosInbox";
import EosEngagement from "@/pages/eos/EosEngagement";
import CustomerCopilot from "@/pages/customer/Copilot";
import CustomerHelp from "@/pages/customer/Help";
import CustomerLearning from "@/pages/customer/Learning";
import CustomerAccount from "@/pages/customer/Account";

function AppRouter() {
  const location = useLocation();
  // Process Emergent OAuth callback synchronously during render (prevents race)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PageTracker />
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<About />} />
        <Route path="/who-its-for" element={<WhoItsFor />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/new" element={<NewAnalysis />} />
        <Route path="/run-analysis" element={<NewAnalysis />} />
        <Route path="/recent" element={<Recent />} />
        <Route path="/report/:id" element={<Report />} />
        <Route path="/report/share/:token" element={<Report shared />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/preview-token" element={<PreviewToken />} />

        {/* Phase 1 — Public Marketing & Education */}
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/learning" element={<Learning />} />
        <Route path="/learning/:slug" element={<LearningArticle />} />
        <Route path="/sample" element={<Sample />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/strategy-call" element={<StrategyCall />} />
        <Route path="/proposal-services" element={<ProposalServices />} />
        <Route path="/services" element={<ProposalServices />} />
        <Route path="/discovery" element={<Discovery />} />
        <Route path="/demo-library" element={<DemoLibrary />} />
        <Route path="/videos" element={<DemoLibrary />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Release 0.1 core — Public read-only Executive Brief share link */}
        <Route path="/brief/:token" element={<PublicBrief />} />

        {/* Release 0.1 — EOS Founder Workspace (ADMIN_KEY gated) */}
        <Route path="/eos" element={<EosGate />} />
        <Route path="/eos/inbox" element={<EosInbox />} />
        <Route path="/eos/engagements/:id" element={<EosEngagement />} />

        {/* Phase 3 — Customer Platform (Magic Link Auth, isolated from internal staff) */}
        <Route path="/customer/login" element={<CustomerAuthProvider><CustomerLogin /></CustomerAuthProvider>} />
        <Route path="/customer/verify" element={<CustomerAuthProvider><CustomerVerify /></CustomerAuthProvider>} />
        <Route
          path="/app"
          element={
            <CustomerAuthProvider>
              <CustomerProtectedRoute>
                <CustomerLayout />
              </CustomerProtectedRoute>
            </CustomerAuthProvider>
          }
        >
          <Route index element={<CustomerDashboard />} />
          <Route path="dashboard" element={<CustomerDashboard />} />
          <Route path="pursuits" element={<CustomerPursuits />} />
          <Route path="pursuits/new" element={<CustomerPursuitNew />} />
          <Route path="pursuits/:id/brief" element={<CustomerPursuitBrief />} />
          <Route path="pursuits/:id" element={<CustomerPursuitDetail />} />
          {/* Story #1: alias legacy /app/reports* to the new pursuits UX */}
          <Route path="reports" element={<CustomerPursuits />} />
          <Route path="reports/:id" element={<CustomerPursuitDetail />} />
          <Route path="copilot" element={<CustomerCopilot />} />
          <Route path="learning" element={<CustomerLearning />} />
          <Route path="account" element={<CustomerAccount />} />
          <Route path="help" element={<CustomerHelp />} />
        </Route>

        {/* Internal Operations Center (RBAC) */}
        <Route
          path="/internal"
          element={<ProtectedRoute><InternalLayout /></ProtectedRoute>}
        >
          <Route index element={<InternalDashboard />} />
          <Route path="dashboard" element={<InternalDashboard />} />
          <Route path="operations" element={<Operations />} />
          <Route path="ai-ops" element={<AiOps />} />
          <Route path="prospects" element={<Prospects />} />
          <Route path="prospects/new" element={<ProspectNew />} />
          <Route path="prospects/:id" element={<ProspectDetail />} />
          <Route path="crm" element={<CRM />} />
          <Route path="knowledge" element={<Knowledge />} />
          <Route path="customers" element={<Customers />} />
          <Route path="team" element={<ProtectedRoute roles={["CEO"]}><Team /></ProtectedRoute>} />
        </Route>
      </Routes>
      <PublicAskAI />
    </>
  );
}

// Show AskAI widget on all PUBLIC routes only (not internal, not login, not report).
function PublicAskAI() {
  const loc = useLocation();
  const p = loc.pathname;
  const hidden = p.startsWith("/internal") || p.startsWith("/app") || p.startsWith("/customer") || p.startsWith("/report") || p.startsWith("/brief") || p.startsWith("/eos") || p === "/login" || p === "/dashboard" || p === "/preview-token" || p.startsWith("/checkout");
  if (hidden) return null;
  return <AskAIWidget />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0a0f18",
            border: "1px solid #334155",
            borderRadius: 0,
            color: "#f8fafc",
            fontFamily: "IBM Plex Mono, monospace",
            fontSize: 12,
          },
        }}
      />
    </div>
  );
}

export default App;
